import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { maxClasses } from 'config/constants'
import { INITIAL_COURSE } from 'experienceComponents/CreateInstanceShared/constants.js'
import {
  getLessonsLink,
  checkLimitReached,
  getInitialSelectedSchool,
} from './data'
import { CreateCourseLauncher } from './CreateCourseLauncher'
import { EditCourseMenu } from './EditCourseMenu'
import ModalRouter from './modalRouter'
import { useCreateCourseHandlers } from './stateManagement'
import { useSideEffects } from './useSideEffects'

const getDefaultInputErrors = () => ({
  className: {
    errors: null,
  },
  selectedGrade: '',
})

const getDefaultState = ({
  courseInstanceName = '',
  selectedSchool = null,
}) => ({
  isFormValid: false,
  inputErrors: getDefaultInputErrors(),
  courseInstanceName,
  sitesWithGrades: [],
  modalContent: null,
  modalFlow: null,
  selectedCourse: INITIAL_COURSE,
  selectedSchool,
  submitAttempts: 0,
})

const createInitialState = props => {
  try {
    return getDefaultState({
      courseInstanceName: props.courseInstanceName,
      selectedSchool: getInitialSelectedSchool(props, {}),
    })
  } catch (error) {
    console.error('Error initializing state:', error)
    return getDefaultState({
      courseInstanceName: props.courseInstanceName,
    })
  }
}

const useStateWithSideEffects = initialStateFactory => {
  const [state, setState] = useState(initialStateFactory)
  const sideEffectsRef = useRef({})

  const updateState = useCallback((newState, sideEffects = {}) => {
    setState(newState)
    sideEffectsRef.current = sideEffects
  }, [])

  return {
    state,
    sideEffectsRef,
    updateState,
  }
}

const CreateCourse = props => {
  const {
    className,
    contentfulCourses,
    course,
    currentSite,
    edit,
    emptyClasses,
    forwardedRef,
    hideLauncher,
    isClassCardMenu,
    lms,
    navigationEvent,
    pathName,
    redirectToMyClasses,
    autoOpen,
  } = props

  const { state, sideEffectsRef, updateState } = useStateWithSideEffects(() =>
    createInitialState(props),
  )

  // Get all handlers from the custom hook
  const {
    resetStateCallback,
    handleCreateCallback,
    handleSubmitCallback,
    goBackOneModalCallback,
    handleClassNameChangeCallback,
    handleGradeChangeCallback,
    handleSiteChangeCallback,
    handleSaveEditCallback,
    handleViewClassCallback,
    handleCancelCallback,
    handleDeleteSubmitCallback,
    handleEditSubmitCallback,
    handleDeleteCallback,
    validateFormCallback,
  } = useCreateCourseHandlers(state, props, updateState)

  // Allow parent to programmatically open the Create modal without clicking a button
  useEffect(() => {
    if (autoOpen) {
      handleCreateCallback()
    }
  }, [autoOpen, handleCreateCallback])

  useSideEffects(
    sideEffectsRef,
    state,
    props,
    handleSiteChangeCallback,
    handleGradeChangeCallback,
    validateFormCallback,
    updateState,
  )

  const {
    courseInstanceName,
    inputErrors,
    isFormValid,
    sitesWithGrades,
    modalContent,
    selectedCourse,
    selectedSchool,
    modalFlow,
    error,
    selectedGrade,
  } = state || {}

  const isLimitReached = useMemo(
    () => checkLimitReached(pathName, lms, maxClasses),
    [pathName, lms],
  )

  const getCourseLessonsLink = useCallback(() => getLessonsLink(pathName), [
    pathName,
  ])

  const modalRouterProps = {
    contentfulCourses,
    courseInstanceName,
    currentSite,
    error,
    getLessonsLink: getCourseLessonsLink,
    goBackOneModal: goBackOneModalCallback,
    handleCancel: handleCancelCallback,
    handleClassNameChange: handleClassNameChangeCallback,
    handleCreate: handleCreateCallback,
    handleDelete: handleDeleteCallback,
    handleGradeChange: handleGradeChangeCallback,
    handleSaveEdit: handleSaveEditCallback,
    handleSiteChange: handleSiteChangeCallback,
    handleSubmit: handleSubmitCallback,
    handleViewClass: handleViewClassCallback,
    inputErrors,
    isClassCardMenu,
    isFormValid,
    limitReached: isLimitReached,
    lms,
    modalContent,
    modalFlow,
    navigationEvent,
    redirectToMyClasses,
    resetState: resetStateCallback,
    selectedCourse,
    selectedGrade,
    selectedSchool,
    sitesWithGrades,
  }

  return (
    <div className={className}>
      <CreateCourseLauncher
        emptyClasses={emptyClasses}
        forwardedRef={forwardedRef}
        hideLauncher={hideLauncher}
        isEditing={edit}
        isLimitReached={isLimitReached}
        onCreate={handleCreateCallback}
      />
      {edit && (
        <EditCourseMenu
          courseInstanceName={courseInstanceName}
          forwardedRef={forwardedRef}
          handleDeleteSubmit={handleDeleteSubmitCallback}
          handleEditSubmit={handleEditSubmitCallback}
          instance={course.instance}
          isClassCardMenu={isClassCardMenu}
        />
      )}
      <ModalRouter {...modalRouterProps} />
    </div>
  )
}

CreateCourse.propTypes = {
  autoOpen: PropTypes.bool,
  className: PropTypes.string,
  classesUrl: PropTypes.string,
  contentfulCourses: PropTypes.array,
  course: PropTypes.object,
  courseInstanceName: PropTypes.string,
  createCourseInstanceV2ProgramFlex: PropTypes.func.isRequired,
  currentSite: PropTypes.shape({
    isSetup: PropTypes.bool,
    siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    siteName: PropTypes.string,
  }),
  deleteCourseInstance: PropTypes.func.isRequired,
  deletingCourseCreatedStatus: PropTypes.func.isRequired,
  edit: PropTypes.bool,
  emptyClasses: PropTypes.bool,
  fetchInstructorData: PropTypes.func.isRequired,
  forwardedRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  hideLauncher: PropTypes.bool,
  isClassCardMenu: PropTypes.bool,
  isCreated: PropTypes.bool,
  limitReached: PropTypes.bool,
  lms: PropTypes.object.isRequired,
  navigationEvent: PropTypes.func,
  pathName: PropTypes.string,
  redirectToMyClasses: PropTypes.func,
  setFocus: PropTypes.func,
  updateCourseInstance: PropTypes.func.isRequired,
  userAccess: PropTypes.object,
  userContext: PropTypes.object,
  userProfile: PropTypes.object,
}

CreateCourse.defaultProps = {
  autoOpen: false,
  hideLauncher: false,
  course: {},
  createCourseInstanceV2ProgramFlex: () => {},
  deletingCourseCreatedStatus: () => {},
}

export default CreateCourse
